import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';

export type PedidoDeConfirmacao = {
  /** Pergunta com o objeto: "Excluir a proposta da Empresa Alfa?" */
  titulo: string;
  /** O que acontece e se dá para desfazer. */
  consequencia: string;
  /** Verbo + objeto: "Excluir proposta", "Marcar saída". */
  confirmar: string;
  /** O que fica como está: "Manter proposta". Padrão: "Cancelar". */
  cancelar?: string;
  /** Ação que apaga ou não tem volta: botão em vermelho. Padrão: true. */
  destrutiva?: boolean;
};

/**
 * Confirmação no padrão do sistema, no lugar do `window.confirm`: diz o que
 * será afetado e a consequência, e os botões dizem a ação. Mesmo uso do
 * `window.confirm`, só que com await:
 *
 *   const { confirmar, dialogo } = useConfirmacao();
 *   if (!(await confirmar({ titulo, consequencia, confirmar: 'Excluir proposta' }))) return;
 *   ...
 *   return <>{...}{dialogo}</>;
 */
export function useConfirmacao() {
  const [pedido, setPedido] = useState<PedidoDeConfirmacao | null>(null);
  const resolver = useRef<((sim: boolean) => void) | null>(null);

  const responder = useCallback((sim: boolean) => {
    resolver.current?.(sim);
    resolver.current = null;
    setPedido(null);
  }, []);

  const confirmar = useCallback((p: PedidoDeConfirmacao) => {
    // Um pedido em aberto que é trocado por outro conta como "não".
    resolver.current?.(false);
    setPedido(p);
    return new Promise<boolean>(resolve => { resolver.current = resolve; });
  }, []);

  const destrutiva = pedido?.destrutiva ?? true;
  const dialogo = (
    <AlertDialog open={!!pedido} onOpenChange={aberto => { if (!aberto) responder(false); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{pedido?.titulo}</AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-line">{pedido?.consequencia}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => responder(false)}>{pedido?.cancelar ?? 'Cancelar'}</AlertDialogCancel>
          <AlertDialogAction
            className={destrutiva ? buttonVariants({ variant: 'destructive' }) : undefined}
            onClick={() => responder(true)}
          >
            {pedido?.confirmar}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirmar, dialogo };
}

let abrirConfirmacao: ((p: PedidoDeConfirmacao) => Promise<boolean>) | null = null;

/** Montado uma vez no App: é o diálogo que o `confirmarAcao` abre. */
export function ConfirmacaoGlobal() {
  const { confirmar, dialogo } = useConfirmacao();
  useEffect(() => {
    abrirConfirmacao = confirmar;
    return () => { if (abrirConfirmacao === confirmar) abrirConfirmacao = null; };
  }, [confirmar]);
  return dialogo;
}

/**
 * O mesmo diálogo do `useConfirmacao`, sem montar nada na tela que pede:
 *
 *   if (!(await confirmarAcao({ titulo, consequencia, confirmar: 'Excluir regra' }))) return;
 *
 * Sem o `ConfirmacaoGlobal` montado (teste de componente isolado), cai no
 * `window.confirm` com o mesmo texto.
 */
export function confirmarAcao(p: PedidoDeConfirmacao): Promise<boolean> {
  if (abrirConfirmacao) return abrirConfirmacao(p);
  return Promise.resolve(window.confirm(`${p.titulo}\n\n${p.consequencia}`));
}
